import { useEffect, useRef, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import "./Navbar.css";

function Navbar() {
  const { user, role, signOut, changePassword } = useAuth();
  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const accountMenuRef = useRef(null);
  const [passwords, setPasswords] = useState({
    current: "",
    next: "",
    confirm: "",
  });
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    if (!isAccountOpen) {
      return undefined;
    }

    const closeAccountMenu = (event) => {
      if (!accountMenuRef.current?.contains(event.target)) {
        setIsAccountOpen(false);
      }
    };

    const closeAccountMenuOnEscape = (event) => {
      if (event.key === "Escape") {
        setIsAccountOpen(false);
      }
    };

    document.addEventListener("mousedown", closeAccountMenu);
    document.addEventListener("keydown", closeAccountMenuOnEscape);

    return () => {
      document.removeEventListener("mousedown", closeAccountMenu);
      document.removeEventListener("keydown", closeAccountMenuOnEscape);
    };
  }, [isAccountOpen]);

  const handlePasswordChange = (event) => {
    const { name, value } = event.target;
    setPasswords((current) => ({ ...current, [name]: value }));
  };

  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordMessage("");
    setPasswordError("");

    if (passwords.next.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }

    if (passwords.next !== passwords.confirm) {
      setPasswordError("New passwords do not match.");
      return;
    }

    setIsSavingPassword(true);

    try {
      await changePassword(passwords.current, passwords.next);
      setPasswords({ current: "", next: "", confirm: "" });
      setPasswordMessage("Password updated successfully.");
    } catch (error) {
      setPasswordError(
        error?.code === "auth/wrong-password"
          ? "Current password is incorrect."
          : "Password could not be updated. Sign in again and retry."
      );
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <header className="navbar">

      {/* =====================================
          LOGO
      ====================================== */}

      <div className="navbar-container">

        <Link to="/" className="navbar-logo">
          <img
            className="navbar-logo-image"
            src="/strides%20logo.png"
            alt="Strides AAR"
          />
        </Link>


        {/* =====================================
            NAVIGATION
        ====================================== */}

        <nav className="navbar-links">

          <NavLink
            to="/"
            className={({ isActive }) =>
              isActive ? "nav-link active" : "nav-link"
            }
          >
            AAR Library
          </NavLink>

          {user && (
            <>
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Dashboard
              </NavLink>

              <NavLink
                to="/action-tracker"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Action Tracker
              </NavLink>

              {(role === "admin" || role === "reviewer" || role === "viewer") && (
                <NavLink
                  to="/manage-aar"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Manage AAR
                </NavLink>
              )}

              {(role === "admin" || role === "reviewer") && (
                <NavLink
                  to="/reviewer-inbox"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Review Inbox
                </NavLink>
              )}

              {role === "admin" && (
                <NavLink
                  to="/activity"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Activity
                </NavLink>
              )}

              {role === "admin" && (
                <NavLink
                  to="/users"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Users
                </NavLink>
              )}

              {role === "admin" && (
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Settings
                </NavLink>
              )}
            </>
          )}

        </nav>


        {/* =====================================
            RIGHT SIDE
        ====================================== */}

        <div className="navbar-right">

          {user ? (
            <div className="navbar-account" ref={accountMenuRef}>
              <button
                type="button"
                className="navbar-avatar"
                onClick={() => {
                  setIsAccountOpen((open) => !open);
                  setPasswordError("");
                  setPasswordMessage("");
                }}
                aria-expanded={isAccountOpen}
                aria-label="Open account menu"
                title={user.email || "Signed-in user"}
              >
                {user.photoURL ? (
                  <img src={user.photoURL} alt="" />
                ) : (
                  (user.displayName || user.email || "U").charAt(0).toUpperCase()
                )}
              </button>
              <button
                type="button"
                className="logout-button"
                onClick={signOut}
                aria-label="Sign out"
                title="Sign out"
              >
                <span aria-hidden="true">↪</span>
              </button>

              {isAccountOpen && (
                <div className="account-dropdown">
                  <div className="account-dropdown-heading">
                    <strong>{user.email}</strong>
                    <small>Change password</small>
                  </div>

                  <form onSubmit={handlePasswordSubmit}>
                    <input
                      name="current"
                      type="password"
                      placeholder="Current password"
                      value={passwords.current}
                      onChange={handlePasswordChange}
                      autoComplete="current-password"
                      required
                    />
                    <input
                      name="next"
                      type="password"
                      placeholder="New password"
                      value={passwords.next}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      minLength="6"
                      required
                    />
                    <input
                      name="confirm"
                      type="password"
                      placeholder="Confirm new password"
                      value={passwords.confirm}
                      onChange={handlePasswordChange}
                      autoComplete="new-password"
                      minLength="6"
                      required
                    />
                    <button type="submit" disabled={isSavingPassword}>
                      {isSavingPassword ? "Saving..." : "Update password"}
                    </button>
                  </form>

                  {passwordMessage && <p className="password-message success">{passwordMessage}</p>}
                  {passwordError && <p className="password-message error" role="alert">{passwordError}</p>}
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="navbar-user">
              Sign in
            </Link>
          )}

        </div>

      </div>

    </header>
  );
}

export default Navbar;